"use client";

import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Image from "next/image";
import { Agent, TopAgentResponse } from "@/types/api";
import { clientFetch, buildTopAgentUrl, formatNilai } from "@/lib/api";

/**
 * Convert Google Drive link to direct image URL
 */
const getImageUrl = (url?: string): string | null => {
  if (!url) return null;

  // Check if it's a Google Drive link
  const driveMatch = url.match(/\/file\/d\/([^/]+)/);
  if (driveMatch) {
    const fileId = driveMatch[1];
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
  }

  return url;
};

/**
 * SectionTopAgent Component
 * Top: Agent Terbaik Kami (All-time best with podium)
 * Bottom: Pantauan Performa Terkini (4 cards grid showing different time periods)
 */

interface AgentWithRank extends Agent {
  rank: number;
}

type TimePeriod = 'today' | 'yesterday' | 'this_week' | 'this_month';

const SectionTopAgent: React.FC = () => {
  // State untuk Agent Terbaik Kami
  const [allTimeAgents, setAllTimeAgents] = useState<AgentWithRank[]>([]);
  const [allTimeLoading, setAllTimeLoading] = useState(true);

  // State untuk Pantauan Performa (4 periods)
  const [performanceData, setPerformanceData] = useState<Record<TimePeriod, AgentWithRank[]>>({
    today: [],
    yesterday: [],
    this_week: [],
    this_month: [],
  });
  const [performancePages, setPerformancePages] = useState<Record<TimePeriod, number>>({
    today: 1,
    yesterday: 1,
    this_week: 1,
    this_month: 1,
  });
  const [performanceMeta, setPerformanceMeta] = useState<Record<TimePeriod, { current_page: number; last_page: number }>>({
    today: { current_page: 1, last_page: 1 },
    yesterday: { current_page: 1, last_page: 1 },
    this_week: { current_page: 1, last_page: 1 },
    this_month: { current_page: 1, last_page: 1 },
  });
  const [performanceLoading, setPerformanceLoading] = useState<Record<TimePeriod, boolean>>({
    today: true,
    yesterday: true,
    this_week: true,
    this_month: true,
  });

  // Fetch Agent Terbaik Kami (All-time)
  useEffect(() => {
    async function fetchAllTimeAgents() {
      try {
        setAllTimeLoading(true);
        const url = buildTopAgentUrl({
          period: 'all-time',
          limit: 10,
          sort_by: 'total_nilai',
        });
        const response = await clientFetch<TopAgentResponse>(url);

        const agentsWithRank = response.data.map((agent, index) => ({
          ...agent,
          rank: index + 1,
        }));

        setAllTimeAgents(agentsWithRank);
      } catch (error) {
        console.error('Failed to fetch all-time agents:', error);
        setAllTimeAgents([]); // Initialize with empty array on error
      } finally {
        setAllTimeLoading(false);
      }
    }

    fetchAllTimeAgents();
  }, []);

  // Fetch Pantauan Performa for all periods
  useEffect(() => {
    const periods: TimePeriod[] = ['today', 'yesterday', 'this_week', 'this_month'];

    periods.forEach((period) => {
      fetchPerformanceData(period, performancePages[period]);
    });
  }, []); // Empty dependency - fetch once on mount

  // Function to fetch performance data for a specific period
  const fetchPerformanceData = async (period: TimePeriod, page: number) => {
    try {
      setPerformanceLoading(prev => ({ ...prev, [period]: true }));

      const url = buildTopAgentUrl({
        period,
        page,
        limit: 5,
        sort_by: 'total_nilai',
      });

      const response = await clientFetch<TopAgentResponse>(url);

      const agentsWithRank = response.data.map((agent, index) => ({
        ...agent,
        rank: (page - 1) * 5 + index + 1,
      }));

      setPerformanceData(prev => ({
        ...prev,
        [period]: agentsWithRank,
      }));

      setPerformanceMeta(prev => ({
        ...prev,
        [period]: {
          current_page: response.meta.current_page,
          last_page: response.meta.last_page,
        },
      }));
    } catch (error) {
      console.error(`Failed to fetch ${period} performance:`, error);
      setPerformanceData(prev => ({
        ...prev,
        [period]: [],
      }));
    } finally {
      setPerformanceLoading(prev => ({ ...prev, [period]: false }));
    }
  };

  // Handle pagination navigation
  const handlePageChange = (period: TimePeriod, direction: 'prev' | 'next') => {
    const currentPage = performancePages[period];
    const newPage = direction === 'next' ? currentPage + 1 : currentPage - 1;

    if (newPage < 1 || newPage > performanceMeta[period].last_page) return;

    setPerformancePages(prev => ({ ...prev, [period]: newPage }));
    fetchPerformanceData(period, newPage);
  };

  const topThreeAllTime = allTimeAgents.slice(0, 3);
  const leaderboardAllTime = allTimeAgents.slice(3);

  // Component for Top Side: Agent Terbaik Kami with Podium
  const AgentTerbaikKami = () => {
    const defaultAgent = (rank: number): AgentWithRank => ({
      agent_id: `default-${rank}`,
      kode: `default-${rank}`,
      nama: "-",
      status: "",
      link_foto: "",
      asal: "-",
      perusahaan: "-",
      mitra: {
        id: "",
        kode: "",
        nama: "-",
      },
      total_tamu: "0",
      total_nilai: "0",
      total_nilai_asli: "0",
      total_transaksi: "0",
      total_transaksi_asli: "0",
      target_bulanan: 0,
      persentase_target: 0,
      rank,
    });

    const topThreeWithDefaults: AgentWithRank[] = [
      topThreeAllTime[1] ?? defaultAgent(2),
      topThreeAllTime[0] ?? defaultAgent(1),
      topThreeAllTime[2] ?? defaultAgent(3),
    ];

    if (allTimeLoading) {
      return (
        <div className="w-full flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fbe64b]"></div>
        </div>
      );
    }

    return (
      <div className="w-full flex flex-col gap-6 items-center">
        {/* Title */}
        <h2 className="font-family-open-sans w-full font-bold lg:text-[20px] md:text-[18px] text-[16px] text-base-black text-center">
          Agent Terbaik Kami
        </h2>

        {/* Top 3 Podium */}
        <div className="w-full">
          <div className="flex flex-col items-center relative">
            <div className="relative w-full h-40 bg-[#fbe64b] overflow-hidden mb-[-150px]">
              <Image
                width={100}
                height={100}
                alt="splash"
                className="w-250 absolute bottom-0"
                src="/image/splash.webp"
              />
            </div>
            <div className="relative flex gap-4 items-between justify-center px-6 w-full">
              {topThreeWithDefaults.map((agent, index) => (
                <div
                  key={agent.kode}
                  className={`flex-1 flex flex-col gap-3 items-center ${
                    index === 1 ? "pt-0" : "pt-16"
                  }`}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  {/* Crown for #1 */}
                  {index === 1 ? (
                    <div className="md:w-20 w-11 h-11 relative mb-[-19px] z-10">
                      <Image
                        height={100}
                        width={100}
                        src="/svg/crown.svg"
                        alt="Crown"
                        className="md:-mt-[11px] mt-[11px]"
                      />
                    </div>
                  ) : null}

                  {/* Profile Image */}
                  <div className="relative w-full md:w-[80px] lg:w-full aspect-square border-2 border-white overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 shadow-md">
                    {getImageUrl(agent.link_foto) ? (
                      <Image
                        src={getImageUrl(agent.link_foto)!}
                        alt={agent.nama}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 80px, 100px"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLDivElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`absolute inset-0 ${getImageUrl(agent.link_foto) ? 'hidden' : 'flex'} items-center justify-center`}>
                      <Icon
                        icon="mdi:account-circle"
                        className="text-gray-400 text-[80px]"
                      />
                    </div>
                  </div>

                  {/* Rank Badge */}
                  <div className="font-family-open-sans flex flex-col items-center">
                    <p className="font-bold text-[20px] text-base-black leading-[17.5px]">
                      #{agent.rank}
                    </p>
                  </div>

                  {/* Agent Details */}
                  <div className="font-family-open-sans flex flex-col gap-2 items-center text-center w-full">
                    <p className="font-normal text-[16px] text-base-black tracking-[0.16px]">
                      {agent.nama}
                    </p>
                    <p className="font-normal text-[13px] text-base-grey">
                      {agent.asal || '-'}
                    </p>

                    {/* Stats - Different layout for #1 vs #2/#3 */}
                    {index === 1 ? (
                      // Winner (#1) - Horizontal layout
                      <div className="flex gap-4 justify-between w-full">
                        <div className="flex flex-col items-center">
                          <p className="font-normal text-[12px] text-base-grey">Tamu</p>
                          <p className="font-bold text-[14px] text-base-black">{agent.total_tamu}</p>
                        </div>
                        <div className="flex flex-col items-center">
                          <p className="font-normal text-[12px] text-base-grey">Penjualan</p>
                          <p className="font-bold text-[14px] text-base-black">
                            {formatNilai(agent.total_nilai, agent.total_nilai_asli)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      // #2 and #3 - Vertical layout
                      <>
                        <div className="flex justify-between w-full px-2">
                          <p className="font-normal text-[12px] text-base-grey">Tamu</p>
                          <p className="font-normal text-[12px] text-base-grey">Penjualan</p>
                        </div>
                        <div className="flex justify-between w-full px-2">
                          <p className="font-bold text-[14px] text-base-black">{agent.total_tamu}</p>
                          <p className="font-bold text-[14px] text-base-black">
                            {formatNilai(agent.total_nilai, agent.total_nilai_asli)}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Table Header */}
        <div className="w-full font-family-open-sans">
          <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-gray-50 border-b-2 border-gray-200 font-semibold text-xs text-gray-600">
            <div className="text-left">Peringkat</div>
            <div className="text-left">Nama Agen</div>
            <div className="text-center">Jumlah Tamu</div>
            <div className="text-right">Total Penjualan</div>
          </div>

          {/* Leaderboard List */}
          {leaderboardAllTime.map((agent, index) => (
            <div
              key={agent.kode}
              className="grid grid-cols-4 gap-2 items-center px-4 py-4 border-b border-[rgba(0,0,0,0.1)] last:border-b-0 hover:bg-gray-50 transition-colors"
              data-aos="fade-up"
              data-aos-delay={index * 50}
            >
              {/* Rank */}
              <div className="flex items-center gap-3">
                <p className="font-bold text-[12px] text-base-black">
                  #{agent.rank}
                </p>
              </div>

              {/* Avatar + Name */}
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#ececf0] flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                  {getImageUrl(agent.link_foto) ? (
                    <Image
                      src={getImageUrl(agent.link_foto)!}
                      alt={agent.nama}
                      fill
                      className="object-cover"
                      sizes="28px"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLParagraphElement;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <p className={`text-[10.5px] text-base-black ${getImageUrl(agent.link_foto) ? 'hidden' : 'block'}`}>
                    {agent.nama.split(" ").map((n: string) => n[0]).join("")}
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="text-[12px] font-semibold text-black leading-tight">
                    {agent.nama}
                  </p>
                  <p className="text-[11px] text-base-grey">{agent.asal}</p>
                </div>
              </div>

              {/* Total Tamu */}
              <div className="text-center">
                <p className="font-semibold text-[14px] text-black">
                  {agent.total_tamu}
                </p>
              </div>

              {/* Total Penjualan */}
              <div className="text-right">
                <p className="font-semibold text-[14px] text-black">
                  {formatNilai(agent.total_nilai, agent.total_nilai_asli)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Component for Performance Card (used in 2x2 grid)
  interface PerformanceCardProps {
    title: string;
    period: TimePeriod;
  }

  const PerformanceCard: React.FC<PerformanceCardProps> = ({ title, period }) => {
    const agents = performanceData[period];
    const loading = performanceLoading[period];
    const meta = performanceMeta[period];
    const currentPage = performancePages[period];

    return (
      <div className="bg-gray-50 rounded-lg p-6 flex flex-col gap-4">
        {/* Card Header with Title and Arrows */}
        <div className="flex items-center justify-between">
          <h3 className="font-family-open-sans font-bold text-[18px] text-base-black">
            {title}
          </h3>
          <div className="flex gap-2">
            <button
              className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(period, 'prev')}
              disabled={currentPage <= 1 || loading}
            >
              <Icon icon="mdi:chevron-left" className="text-lg text-gray-600" />
            </button>
            <button
              className="p-1.5 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(period, 'next')}
              disabled={currentPage >= meta.last_page || loading}
            >
              <Icon icon="mdi:chevron-right" className="text-lg text-gray-600" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="font-family-open-sans">
          {/* Table Header */}
          <div className="grid grid-cols-4 gap-2 pb-2 border-b border-gray-300 text-[11px] text-gray-600">
            <div className="text-left">Peringkat</div>
            <div className="text-left">Nama Agen</div>
            <div className="text-center">Jumlah Tamu</div>
            <div className="text-right">Total Penjualan</div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">
              Tidak ada data
            </div>
          ) : (
            /* Table Rows */
            agents.map((agent) => (
              <div
                key={agent.kode}
                className="grid grid-cols-4 gap-2 items-center py-3 border-b border-gray-200 last:border-b-0"
              >
                {/* Rank */}
                <div className="text-left">
                  <p className="font-bold text-[14px] text-base-black">
                    #{agent.rank}
                  </p>
                </div>

                {/* Avatar + Name + Location */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#ececf0] flex items-center justify-center flex-shrink-0 relative overflow-hidden">
                    {getImageUrl(agent.link_foto) ? (
                      <Image
                        src={getImageUrl(agent.link_foto)!}
                        alt={agent.nama}
                        fill
                        className="object-cover"
                        sizes="32px"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLParagraphElement;
                          if (fallback) fallback.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <p className={`text-[11px] text-base-black ${getImageUrl(agent.link_foto) ? 'hidden' : 'block'}`}>
                      {agent.nama.split(" ").map((n: string) => n[0]).join("")}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[13px] font-semibold text-black leading-tight">
                      {agent.nama}
                    </p>
                    <p className="text-[11px] text-base-grey">{agent.asal}</p>
                  </div>
                </div>

                {/* Total Tamu */}
                <div className="text-center">
                  <p className="font-medium text-[13px] text-black">
                    {agent.total_tamu}
                  </p>
                </div>

                {/* Total Penjualan */}
                <div className="text-right">
                  <p className="font-medium text-[13px] text-black">
                    {formatNilai(agent.total_nilai, agent.total_nilai_asli)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Bottom Side: 4 Cards in 2x2 Grid
  const PantauanPerformaTerkini = () => {
    return (
      <div className="w-full flex flex-col gap-6">
        {/* Title */}
        <h2 className="font-family-open-sans font-bold lg:text-[20px] md:text-[18px] text-[16px] text-base-black text-center">
          Pantauan Performa Terkini
        </h2>

        {/* 2x2 Grid of Performance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PerformanceCard title="Hari Ini" period="today" />
          <PerformanceCard title="Kemarin" period="yesterday" />
          <PerformanceCard title="Minggu Ini" period="this_week" />
          <PerformanceCard title="Bulan Ini" period="this_month" />
        </div>
      </div>
    );
  };

  return (
    <div
      data-aos="fade-up"
      className="lg:py-[52px] md:py-8 py-6 lg:px-0 md:px-12 px-6 flex flex-col gap-12 items-center"
    >
      {/* Container */}
      <div className="w-full flex flex-col gap-12 items-center max-w-6xl mx-auto">
        {/* Title Section */}
        <div className="w-full flex flex-col items-center gap-1">
          <h2 className="lg:text-[40px] md:text-[32px] text-[24px] text-base-black text-center font-bold">
            Agent Pekalen Rafting
          </h2>
          <p className="text-base-grey font-family-open-sans text-center md:text-base text-[13px]">
            Prestasi nyata agen yang Anda rekrut. Siapa paling banyak bawa tamu, siapa paling tinggi nilainya, semua terangkum di sini.
          </p>
        </div>

        {/* Content - Vertical Stack */}
        <div className="flex flex-col gap-12 w-full">
          {/* Section 1: Agent Terbaik Kami - Full Width */}
          <div className="w-full">
            <AgentTerbaikKami />
          </div>

          {/* Section 2: Pantauan Performa Terkini - Full Width */}
          <div className="w-full">
            <PantauanPerformaTerkini />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionTopAgent;
